import React, { useState, useEffect, useRef } from 'react';
import { buildApiUrl } from './config';

const ChatWindow = ({ 
  currentUserId, 
  recipientId, 
  recipientName, 
  isGroup, 
  formId, 
  students,
  chatType = 'parent', // 'parent' for teacher-parent, 'student' for teacher-student
  onNewMessage // Callback when new message received via WebSocket
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  // Check if WebSocket was previously disabled in this session
  const [wsDisabled, setWsDisabled] = useState(() => {
    return sessionStorage.getItem('chatWsDisabled') === 'true';
  });
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const loadMessagesRef = useRef(null);
  const serverRef = useRef(buildApiUrl(""));
  const server = serverRef.current;
  const messagesContainerRef = useRef(null);
  const userScrolledRef = useRef(false);
  const initialLoadRef = useRef(true);
  const prevMessagesLengthRef = useRef(0);

  // Auto-scroll to bottom only when appropriate
  const scrollToBottom = (force = false) => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    if (force || (isAtBottom && !userScrolledRef.current)) {
      messagesEndRef.current?.scrollIntoView({ behavior: force ? "auto" : "smooth" });
    }
  };

  // Track user scroll
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    userScrolledRef.current = !isAtBottom;
  };

  // Only scroll on initial load
  useEffect(() => {
    if (initialLoadRef.current && messages.length > 0) {
      scrollToBottom(true);
      initialLoadRef.current = false;
    } else if (messages.length > prevMessagesLengthRef.current) {
      // New message arrived - only scroll if user is at bottom
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current || {};
      const isAtBottom = scrollHeight && (scrollHeight - scrollTop - clientHeight < 100);
      if (isAtBottom) {
        scrollToBottom(false);
      }
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages]);

  // Connect to WebSocket
  useEffect(() => {
    if (!currentUserId || wsDisabled) return;

    // Build WebSocket URL from server base
    const encodedUserId = encodeURIComponent(currentUserId);
    const wsBase = server.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
    const wsUrl = `${wsBase}/ws/chat/${encodedUserId}`;
    
    let websocket = null;
    let cancelled = false;

    // Small delay to prevent flickering on rapid mount/unmount
    const connectTimeout = setTimeout(() => {
      if (cancelled) return;
      
      try {
        websocket = new WebSocket(wsUrl);
      } catch (e) {
        // WebSocket constructor failed - disable and fall back to polling
        console.log('💬 WebSocket not available, using polling mode');
        setWsDisabled(true);
        sessionStorage.setItem('chatWsDisabled', 'true');
        return;
      }
      
      let connectionSucceeded = false;

      websocket.onopen = () => {
        if (cancelled) { websocket.close(); return; }
        console.log('💬 Connected to chat WebSocket - Real-time mode enabled');
        connectionSucceeded = true;
        setConnected(true);
        setWs(websocket);
      };

      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_message') {
            setMessages(prev => [...prev, data.message]);
            if (onNewMessage) onNewMessage(data.message);
          } else if (data.type === 'message_sent') {
            console.log('✅ Message sent:', data.message_id);
          } else if (data.type === 'error') {
            console.error('❌ Chat error:', data.error);
          }
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      };

      websocket.onerror = () => {
        // Silently disable WebSocket and fall back to polling
        if (!connectionSucceeded) {
          setWsDisabled(true);
          sessionStorage.setItem('chatWsDisabled', 'true');
        }
      };

      websocket.onclose = () => {
        setConnected(false);
        setWs(null);
      };
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(connectTimeout);
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, [currentUserId, wsDisabled]);

  // Load existing messages
  useEffect(() => {
    if (!recipientId && !isGroup) return;

    const loadMessages = async () => {
      try {
        let response;
        if (chatType === 'student') {
          // Use student chat endpoints
          const params = new URLSearchParams({
            is_group: isGroup,
            ...(recipientId && !isGroup ? { 
              user_id: recipientId,
              current_user_id: currentUserId 
            } : {})
          });
          response = await fetch(`${server}/api/student-chat/messages/${formId}?${params}`);
        } else {
          // Use parent chat endpoints
          if (!isGroup) {
            response = await fetch(`${server}/api/chat/messages/${currentUserId}/${recipientId}`);
          }
        }
        
        if (response) {
          const data = await response.json();
          setMessages(data.messages || []);
          
          // Mark messages as read after loading them
          if (chatType === 'student' && !isGroup && recipientId) {
            markMessagesAsRead();
          }
        }
      } catch (error) {
        // Silently handle errors
      }
    };

    // Store load function in ref for polling to use
    loadMessagesRef.current = loadMessages;

    // Initial load when chat changes
    loadMessages();
  }, [recipientId, currentUserId, isGroup, formId, chatType]);
  
  // Mark messages as read for student chat
  const markMessagesAsRead = async () => {
    if (chatType !== 'student' || !recipientId || isGroup) return;
    
    try {
      const response = await fetch(`${server}/api/student-chat/mark-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_id: formId,
          user_id: currentUserId, // teacher email
          contact_id: recipientId, // student school_id
          contact_type: 'student' // marking student messages as read
        })
      });
      
      const result = await response.json();
      
      // Notify parent component to refresh unread counts if callback provided
      if (onNewMessage && result.success) {
        onNewMessage({ type: 'messages_read', student_id: recipientId });
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Separate effect for polling - only depends on connected state
  useEffect(() => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only poll if WebSocket is not connected
    if (!connected && loadMessagesRef.current) {
      pollingIntervalRef.current = setInterval(() => {
        if (loadMessagesRef.current) {
          loadMessagesRef.current();
        }
      }, 3000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [connected]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 50MB for videos, 10MB for others)
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for videos, 10MB for others
    if (file.size > maxSize) {
      alert(`File is too large. Maximum size is ${isVideo ? '50MB' : '10MB'}.`);
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'application/zip',
      'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
    
    if (!allowedTypes.includes(file.type)) {
      alert('File type not supported. Please upload images, videos, PDFs, Office documents, or ZIP files.');
      return;
    }

    setSelectedFile(file);
  };

  // Upload file to server
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    setUploadingFile(true);
    setUploadProgress(0);

    try {
      const response = await fetch(`${server}/api/chat/upload-attachment`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('File upload failed');
      }

      const result = await response.json();
      setUploadProgress(100);
      setUploadingFile(false);
      
      return {
        url: result.url,
        filename: result.original_filename,
        size: result.size
      };
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      setUploadingFile(false);
      alert('Failed to upload file. Please try again.');
      return null;
    }
  };

  // Remove selected file
  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    let attachmentData = null;

    // Upload file first if selected
    if (selectedFile) {
      attachmentData = await uploadFile(selectedFile);
      if (!attachmentData) {
        // Upload failed, don't send message
        return;
      }
    }

    // Determine sender role based on context
    const senderRole = chatType === 'student' ? 
      (localStorage.getItem('studentId') ? 'student' : 'teacher') : 
      'teacher';

    const messageData = {
      type: 'send_message',
      sender_id: currentUserId,
      receiver_id: isGroup ? `group_${formId}` : recipientId,
      content: newMessage,
      sender_name: localStorage.getItem('teacherEmail') || localStorage.getItem('studentName') || currentUserId,
      receiver_name: recipientName,
      sender_role: senderRole,
      receiver_role: isGroup ? 'group' : (chatType === 'student' ? 'teacher' : 'student'),
      is_group: isGroup,
      form_id: formId,
      timestamp: new Date().toISOString()
    };

    // Try WebSocket first, fall back to REST API if not available
    if (ws && ws.readyState === WebSocket.OPEN) {
      console.log('📤 Sending via WebSocket');
      ws.send(JSON.stringify(messageData));
    } else {
      console.log('📤 Sending via REST API (WebSocket not available)');
      try {
        // Create FormData for the backend endpoint
        const formData = new FormData();
        
        if (chatType === 'student') {
          // Student chat endpoint
          formData.append('form_id', formId);
          formData.append('sender_id', currentUserId);
          formData.append('sender_role', senderRole);
          formData.append('sender_name', localStorage.getItem('studentName') || currentUserId);
          formData.append('content', newMessage.trim() || (attachmentData ? '📎 Attachment' : ''));
          formData.append('is_group_message', isGroup);
          if (!isGroup && recipientId) {
            formData.append('receiver_id', recipientId);
            formData.append('receiver_role', 'teacher');
            formData.append('receiver_name', recipientName);
          }
          // Add attachment data if available
          if (attachmentData) {
            formData.append('attachment_url', attachmentData.url);
            formData.append('attachment_filename', attachmentData.filename);
            formData.append('attachment_size', attachmentData.size);
          }
        } else {
          // Parent chat endpoint
          formData.append('sender_id', currentUserId);
          formData.append('receiver_id', isGroup ? `group_${formId}` : recipientId);
          formData.append('content', newMessage.trim() || (attachmentData ? '📎 Attachment' : ''));
          formData.append('sender_name', localStorage.getItem('teacherEmail') || currentUserId);
          formData.append('receiver_name', recipientName);
          formData.append('sender_role', 'teacher');
          formData.append('receiver_role', isGroup ? 'group' : 'student');
          formData.append('is_group', isGroup);
          if (formId) formData.append('form_id', formId);
          // Add attachment data if available
          if (attachmentData) {
            formData.append('attachment_url', attachmentData.url);
            formData.append('attachment_filename', attachmentData.filename);
            formData.append('attachment_size', attachmentData.size);
          }
        }
        
        const endpoint = chatType === 'student' ? '/api/student-chat/send-message' : '/api/chat/send-message';
        const response = await fetch(`${server}${endpoint}`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Server error:', errorText);
          throw new Error('Failed to send message');
        }
        
        const result = await response.json();
        console.log('✅ Message sent via REST:', result);
      } catch (error) {
        console.error('❌ Error sending message:', error);
        alert('Failed to send message. Please try again.');
        return;
      }
    }

    // Add to local messages immediately for better UX
    const localMessage = {
      id: `temp_${Date.now()}`,
      sender: currentUserId,
      receiver: isGroup ? `group_${formId}` : recipientId,
      content: newMessage,
      timestamp: new Date().toISOString(),
      type: 'text',
      is_group: isGroup
    };
    setMessages(prev => [...prev, localMessage]);
    setNewMessage('');
    removeSelectedFile(); // Clear file selection after sending
    
    // Force scroll to bottom when user sends a message
    setTimeout(() => scrollToBottom(true), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Delete message function
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    setDeletingMessageId(messageId);
    try {
      // Determine user role
      const userRole = localStorage.getItem('studentId') ? 'student' : 'teacher';
      
      const endpoint = chatType === 'student' 
        ? `/api/student-chat/delete-message/${messageId}?user_id=${encodeURIComponent(currentUserId)}&user_role=${userRole}`
        : `/api/chat/delete-message/${messageId}?user_id=${encodeURIComponent(currentUserId)}&user_role=${userRole}`;
      
      const response = await fetch(`${server}${endpoint}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove message from local state
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    } finally {
      setDeletingMessageId(null);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (!recipientId && !isGroup) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-lg font-medium">Select a student to start chatting</p>
          <p className="text-sm mt-2">or select the class group for group messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Messages Area */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50"
      >
        {messages.length === 0 ? (
          <div className="text-center text-gray-400 mt-8">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isOwnMessage = msg.sender === currentUserId;
            
            // Get sender name - for group chats, show the sender's name
            let senderName = null;
            if (isGroup && !isOwnMessage) {
              const rawName = msg.sender_name || msg.sender || 'Unknown';
              
              // Format teacher names with Mr/Ms prefix and last name
              if (msg.sender_role === 'teacher') {
                // Check if rawName is an email (contains @)
                let displayName = rawName;
                
                // If it's an email, try to extract a name from it
                if (rawName.includes('@')) {
                  // For now, if we only have email, show it as is
                  displayName = rawName.split('@')[0].replace(/[._]/g, ' ');
                }
                
                // Check if name already has a title
                if (!displayName.match(/^(Mr|Ms|Mrs|Dr|Miss)\.?\s/i)) {
                  // Split full name to get first and last name
                  const nameParts = displayName.trim().split(' ');
                  const firstName = nameParts[0].toLowerCase();
                  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
                  
                  // Determine prefix based on first name
                  const commonFemaleNames = ['sarah', 'emily', 'jennifer', 'jessica', 'lisa', 'mary', 'patricia', 'linda', 'barbara', 'susan', 'karen', 'nancy', 'betty', 'margaret', 'sandra', 'ashley', 'dorothy', 'kimberly', 'donna', 'michelle', 'carol', 'amanda', 'melissa', 'deborah', 'stephanie', 'rebecca', 'laura', 'sharon', 'cynthia', 'kathleen', 'helen', 'amy', 'anna', 'angela', 'ruth', 'brenda', 'pamela', 'nicole', 'katherine', 'samantha', 'christine', 'catherine', 'virginia', 'debra', 'rachel', 'janet', 'emma', 'carolyn', 'maria', 'heather', 'diane', 'julie', 'joyce', 'evelyn', 'joan', 'victoria', 'kelly', 'christina', 'lauren', 'frances', 'martha', 'judith', 'cheryl', 'megan', 'andrea', 'ann', 'alice', 'jean', 'doris', 'jacqueline', 'kathryn', 'hannah', 'olivia', 'gloria', 'marie', 'teresa', 'sara', 'janice', 'julia', 'grace', 'judy', 'theresa', 'rose', 'beverly', 'denise', 'marilyn', 'amber', 'danielle', 'brittany', 'diana', 'abigail', 'jane', 'natalie', 'lori', 'tiffany', 'alexis', 'kayla', 'samah'];
                  
                  const prefix = commonFemaleNames.includes(firstName) ? 'Ms' : 'Mr';
                  senderName = `${prefix} ${lastName}`;
                } else {
                  senderName = displayName;
                }
              } else {
                senderName = rawName;
              }
            }
            
            return (
              <div
                key={msg.id || index}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div className="flex flex-col max-w-xs lg:max-w-md">
                  {/* Sender name for group chats */}
                  {senderName && (
                    <p className="text-xs text-gray-500 mb-1 ml-2">
                      {senderName}
                    </p>
                  )}
                  
                  <div className="relative group">
                    <div
                      className={`px-4 py-3 rounded-2xl shadow-sm ${
                        isOwnMessage
                          ? 'bg-blue-600 text-white rounded-br-none'
                          : 'bg-white text-gray-800 rounded-bl-none border border-gray-200'
                      }`}
                    >
                      {/* Message text */}
                      {msg.content && <p className="text-sm break-words">{msg.content}</p>}
                      
                      {/* Attachment display */}
                      {msg.attachment_url && (
                        <div className="mt-2">
                          {/* Check if it's an image */}
                          {msg.attachment_filename && /\.(jpg|jpeg|png|gif)$/i.test(msg.attachment_filename) ? (
                            <a
                              href={`${server}${msg.attachment_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={`${server}${msg.attachment_url}`}
                                alt={msg.attachment_filename}
                                className="max-w-full rounded-lg border border-gray-300 hover:opacity-90 transition-opacity"
                                style={{ maxHeight: '300px', objectFit: 'contain' }}
                              />
                              <p className={`text-xs mt-1 ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                                {msg.attachment_filename} • {formatFileSize(msg.attachment_size)}
                              </p>
                            </a>
                          ) : msg.attachment_filename && /\.(mp4|webm|mov|avi|mkv)$/i.test(msg.attachment_filename) ? (
                            // Video file
                            <div className="block">
                              <video
                                controls
                                className="max-w-full rounded-lg border border-gray-300"
                                style={{ maxHeight: '300px' }}
                              >
                                <source src={`${server}${msg.attachment_url}`} type={
                                  msg.attachment_filename.endsWith('.mp4') ? 'video/mp4' :
                                  msg.attachment_filename.endsWith('.webm') ? 'video/webm' :
                                  msg.attachment_filename.endsWith('.mov') ? 'video/quicktime' :
                                  msg.attachment_filename.endsWith('.avi') ? 'video/x-msvideo' :
                                  'video/mp4'
                                } />
                                Your browser does not support the video tag.
                              </video>
                              <p className={`text-xs mt-1 ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                                {msg.attachment_filename} • {formatFileSize(msg.attachment_size)}
                              </p>
                            </div>
                          ) : (
                            // Non-image/video file
                            <a
                              href={`${server}${msg.attachment_url}`}
                              download={msg.attachment_filename}
                              className={`flex items-center space-x-2 p-2 rounded ${
                                isOwnMessage ? 'bg-blue-700' : 'bg-gray-100'
                              } hover:opacity-80 transition-opacity`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{msg.attachment_filename}</p>
                                <p className="text-xs opacity-75">{formatFileSize(msg.attachment_size)}</p>
                              </div>
                            </a>
                          )}
                        </div>
                      )}
                      
                      <p
                        className={`text-xs mt-1 ${
                          isOwnMessage ? 'text-blue-100' : 'text-gray-400'
                        }`}
                      >
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                    
                    {/* Delete button - for own messages, or for teachers in group chats */}
                    {(() => {
                      const userRole = localStorage.getItem('studentId') ? 'student' : 'teacher';
                      const isTeacherInGroup = userRole === 'teacher' && isGroup;
                      const canDelete = (isOwnMessage || isTeacherInGroup) && msg.id && !msg.id.toString().startsWith('temp_');
                      
                      return canDelete && (
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          disabled={deletingMessageId === msg.id}
                          className={`absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 rounded-full ${
                            deletingMessageId === msg.id
                              ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-red-100 hover:bg-red-200 text-red-600'
                        }`}
                        title="Delete message"
                      >
                        {deletingMessageId === msg.id ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
        {/* File upload preview */}
        {selectedFile && (
          <div className="mb-3 flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <button
              onClick={removeSelectedFile}
              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
              title="Remove file"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Upload progress */}
        {uploadingFile && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Uploading file...</span>
              <span className="text-sm font-medium text-blue-600">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
        
        <div className="flex items-end space-x-2">
          {/* File upload button */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile}
            className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            title="Attach file"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>
          
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Message ${isGroup ? 'the class' : recipientName}...`}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-full resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="1"
            style={{ maxHeight: '120px', minHeight: '40px' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            onClick={sendMessage}
            disabled={(!newMessage.trim() && !selectedFile) || uploadingFile}
            className="p-2.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex-shrink-0"
            title="Send"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
